import { useCallback } from 'react';

import { createHISIOpenInviteFlagKey, defaultOpenInviteClient } from '../../clients';
import { useViralSettingsHaveISeenIt } from '../ViralSettings';
import { type InviteProcessingResponse, InviteProcessingResponseStatus } from '../../types';
import { type UpdateOpenInviteResponse } from '../ViralSettings/types';
import { isAcceptableFailure, makeAri, resolveError } from '../../utils';
import { useAnalytics } from '../../analytics';
import {
	inviteFormSubmitted,
	inviteFormSubmittedTimings,
	inviteSettingsUpdated,
} from '../../analytics/ufoExperiences';
import { InvitePeopleAnalytics } from './InvitePeopleAnalytics';
import { useInvitePeopleContext } from './InvitePeopleContext';

export type OpenInviteHookProps = {
	showOpenInvite: boolean;
	enableOpenInvite: boolean;
	selectedProducts: string[];
	isViralSettingsEnabled: boolean;
};

const SKIPPED_RESPONSE: InviteProcessingResponse = {
	responseStatus: InviteProcessingResponseStatus.SKIPPED,
	flags: [],
};

const useOpenInvite = ({
	showOpenInvite,
	enableOpenInvite,
	selectedProducts,
	isViralSettingsEnabled,
}: OpenInviteHookProps) => {
	const analytics = useAnalytics(InvitePeopleAnalytics);
	const invitePeopleData = useInvitePeopleContext();
	const cloudId = invitePeopleData.cloudId ?? '';
	const { setViralSettingsHaveISeenIt } = useViralSettingsHaveISeenIt();
	const processOpenInviteSettings = useCallback(async (): Promise<
		InviteProcessingResponse<UpdateOpenInviteResponse, unknown>
	> => {
		// Adding empty flag array in preparation for flags to be refactored from getViralSettingsFlags in near future
		if (!isViralSettingsEnabled || selectedProducts.length !== 1 || !cloudId) {
			return SKIPPED_RESPONSE;
		}
		const product = selectedProducts[0];
		if (!enableOpenInvite && showOpenInvite) {
			// If unchecked and open invite is shown
			const openInviteKey = createHISIOpenInviteFlagKey(product, cloudId);
			setViralSettingsHaveISeenIt(openInviteKey, product);
			return SKIPPED_RESPONSE;
		}

		if (!enableOpenInvite) {
			return SKIPPED_RESPONSE;
		}

		const productAri = makeAri(product, cloudId);

		try {
			inviteSettingsUpdated.start();
			inviteFormSubmitted.mark(inviteFormSubmittedTimings.updateOpenInviteSettings.startMark);

			const response = await defaultOpenInviteClient.enableOpenInvite(product, cloudId);

			analytics.userInviteSettingsSucceededSLO({
				newState: 'enabled',
				productAri,
			});
			inviteSettingsUpdated.success();

			return {
				responseStatus: InviteProcessingResponseStatus.SUCCESS,
				response: response,
				flags: [],
			} as InviteProcessingResponse;
		} catch (err: unknown) {
			const { responseCode, errorMessage, error } = resolveError(err);

			analytics.userInviteSettingsFailed({
				proposedNewState: 'enabled',
				productAri,
				responseCode,
				errorMessage,
			});

			if (isAcceptableFailure(errorMessage, responseCode)) {
				// user invite setting state was not changed so we will send null as the newState attribute
				analytics.userInviteSettingsSucceededSLO({
					newState: null,
					productAri,
				});
				inviteSettingsUpdated.success();
			} else {
				analytics.userInviteSettingsFailedSLO({
					proposedNewState: 'enabled',
					productAri,
					responseCode,
					errorMessage,
				});
				inviteSettingsUpdated.failure();
			}
			return {
				responseStatus: InviteProcessingResponseStatus.ERROR,
				error: error,
				flags: [],
			};
		} finally {
			inviteFormSubmitted.mark(inviteFormSubmittedTimings.updateOpenInviteSettings.endMark);
		}
	}, [
		isViralSettingsEnabled,
		selectedProducts,
		cloudId,
		enableOpenInvite,
		showOpenInvite,
		setViralSettingsHaveISeenIt,
		analytics,
	]);

	return {
		processOpenInviteSettings,
	};
};

export default useOpenInvite;
